import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import {
  getFirestore,
  runTransaction,
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  setDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  arrayUnion,
  arrayRemove,
  deleteField,
  enableIndexedDbPersistence,
} from 'firebase/firestore';
import { slotFieldPath, normalizeSlotKey, tripPatchFields, getSlotView } from '../utils/tripLogic';

// Firebase設定（環境変数のみ参照。直書き禁止 — .env / CI の環境変数に設定すること）
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// 環境変数の未設定チェック（ビルド時に値が埋め込まれるため、欠けていれば起動時に即エラー）
const missingKeys = Object.entries(firebaseConfig)
  .filter(([, value]) => !value)
  .map(([key]) => `REACT_APP_FIREBASE_${key.replace(/([A-Z])/g, '_$1').toUpperCase()}`);
if (missingKeys.length > 0) {
  throw new Error(
    `Firebase環境変数が未設定です: ${missingKeys.join(', ')}\n` +
      'プロジェクトルートの .env.example を参考に .env を作成し、npm start / npm run build をやり直してください。'
  );
}

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// Firestoreオフラインキャッシュを有効化
enableIndexedDbPersistence(db).catch((err) => {
  if (err.code === 'failed-precondition') {
    console.warn('Firestore persistence failed: multiple tabs open');
  } else if (err.code === 'unimplemented') {
    console.warn('Firestore persistence not supported in this browser');
  }
});

const googleProvider = new GoogleAuthProvider();

// 認証
export const signInWithGoogle = () => signInWithPopup(auth, googleProvider);
export const logOut = () => signOut(auth);

// タスクCRUD操作
const getUserTasksRef = (userId) => collection(db, 'users', userId, 'tasks');

// リアルタイムリスナー（スマホ・PC間同期用）
export const subscribeToTasks = (userId, callback) => {
  const q = query(getUserTasksRef(userId), orderBy('order', 'asc'));
  return onSnapshot(q, (snapshot) => {
    const tasks = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(tasks);
  }, (err) => {
    console.error('Snapshot listener error:', err);
  });
};

export const fetchTasks = async (userId) => {
  const q = query(getUserTasksRef(userId), orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const addTask = async (userId, task) => {
  const ref = await addDoc(getUserTasksRef(userId), {
    ...task,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
};

export const updateTask = async (userId, taskId, updates) => {
  const ref = doc(db, 'users', userId, 'tasks', taskId);
  await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });
};

export const deleteTask = async (userId, taskId) => {
  const ref = doc(db, 'users', userId, 'tasks', taskId);
  await deleteDoc(ref);
};

export const batchUpdateTasks = async (userId, updates) => {
  const batch = writeBatch(db);
  updates.forEach(({ id, data }) => {
    const ref = doc(db, 'users', userId, 'tasks', id);
    batch.update(ref, { ...data, updatedAt: serverTimestamp() });
  });
  await batch.commit();
};

export const batchDeleteTasks = async (userId, taskIds) => {
  const batch = writeBatch(db);
  taskIds.forEach((id) => {
    const ref = doc(db, 'users', userId, 'tasks', id);
    batch.delete(ref);
  });
  await batch.commit();
};

// 投稿リクエスト
export const createPostRequest = async (userId, content, chatUrl) => {
  const ref = collection(db, 'users', userId, 'postRequests');
  const docRef = await addDoc(ref, {
    content,
    status: 'pending',
    chatUrl,
    createdAt: serverTimestamp(),
  });
  return docRef.id;
};

// アプリ日付の管理（Firestoreに保存して端末間で共有）
const getUserSettingsRef = (userId) => doc(db, 'users', userId, 'settings', 'appState');

export const getAppDate = async (userId) => {
  const ref = getUserSettingsRef(userId);
  const snap = await getDoc(ref);
  if (snap.exists() && snap.data().appDate) {
    return snap.data().appDate;
  }
  // 初回は今日のローカル日付（toISOStringはUTCで最大9時間ズレるため使わない）
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  await setDoc(ref, { appDate: today }, { merge: true });
  return today;
};

export const updateAppDate = async (userId, newDate) => {
  const ref = getUserSettingsRef(userId);
  await setDoc(ref, { appDate: newDate, updatedAt: serverTimestamp() }, { merge: true });
};

// アプリ日付のリアルタイムリスナー
export const subscribeToAppDate = (userId, callback) => {
  const ref = getUserSettingsRef(userId);
  return onSnapshot(ref, (snap) => {
    if (snap.exists() && snap.data().appDate) {
      callback(snap.data().appDate);
    }
  });
};

// ============================================================
// 買い物リスト（shoppingList）— 既存タスクとは独立
// 構造: users/{uid}/shoppingList/{itemId}
// フィールド: name(string), bought(bool), createdAt, updatedAt(serverTimestamp)
// ============================================================
const getUserShoppingRef = (userId) => collection(db, 'users', userId, 'shoppingList');

export const subscribeToShoppingList = (userId, callback) => {
  const q = query(getUserShoppingRef(userId), orderBy('createdAt', 'asc'));
  return onSnapshot(
    q,
    (snapshot) => {
      const items = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(items);
    },
    (err) => {
      console.error('Shopping snapshot listener error:', err);
    }
  );
};

export const addShoppingItem = async (userId, name) => {
  const ref = await addDoc(getUserShoppingRef(userId), {
    name,
    bought: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
};

export const updateShoppingItem = async (userId, itemId, updates) => {
  const ref = doc(db, 'users', userId, 'shoppingList', itemId);
  await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });
};

export const deleteShoppingItem = async (userId, itemId) => {
  const ref = doc(db, 'users', userId, 'shoppingList', itemId);
  await deleteDoc(ref);
};

export const batchDeleteShoppingItems = async (userId, itemIds) => {
  if (!itemIds || itemIds.length === 0) return;
  const batch = writeBatch(db);
  itemIds.forEach((id) => {
    const ref = doc(db, 'users', userId, 'shoppingList', id);
    batch.delete(ref);
  });
  await batch.commit();
};

// 日付ベースのタスク移動（futureタスクの自動移動）
// appDate: 「この日付以前のfutureタスク」を今日に移動する基準日（YYYY-MM-DD）
// forceIds: 日付に関係なく強制的にtodayに移動させるタスクIDの配列（手動選択用）
//
// 実装方針：
// 複合インデックス(category+date)不要にするため、categoryだけで絞り込んでから
// メモリ上でdate条件とforceIds条件を評価する。future件数は多くないので十分軽量。
// ローカル日付(YYYY-MM-DD)を返す（toISOStringはUTCで最大9時間ズレるため使わない）
function localDateStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export const moveDueTasksToToday = async (userId, appDate, forceIds = []) => {
  const targetDate = appDate || localDateStr();
  const forceIdSet = new Set(forceIds);

  const q = query(
    getUserTasksRef(userId),
    where('category', '==', 'future')
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty && forceIdSet.size === 0) return [];

  const toMove = [];
  snapshot.docs.forEach((d) => {
    const data = d.data();
    const byDate = !!data.date && data.date <= targetDate;
    const byForce = forceIdSet.has(d.id);
    if (byDate || byForce) {
      toMove.push(d);
    }
  });

  if (toMove.length === 0) return [];

  const batch = writeBatch(db);
  const movedTasks = [];
  toMove.forEach((d) => {
    batch.update(d.ref, {
      category: 'today',
      date: null,
      updatedAt: serverTimestamp(),
    });
    movedTasks.push({ id: d.id, ...d.data(), category: 'today', date: null });
  });
  await batch.commit();
  return movedTasks;
};

// ============================================================
// 定期タスク（recurringTasks）— 繰り返し定義
// 構造: users/{uid}/recurringTasks/{id}
// フィールド:
//   title(string)
//   type('daily' | 'weekly' | 'monthly')
//   weekdays(number[] 0=日..6=土)  ※type==='weekly'で使用
//   dayOfMonth(number 1-31)        ※type==='monthly'で使用
//   order(number)
//   lastGeneratedDate(string 'YYYY-MM-DD' | null)  ※同日二重生成の防止
//   createdAt, updatedAt(serverTimestamp)
// ============================================================
const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

const getUserRecurringRef = (userId) => collection(db, 'users', userId, 'recurringTasks');

// 日付文字列(YYYY-MM-DD)をローカルDateに変換（タイムゾーン安全）
function parseDateStr(dateStr) {
  const parts = dateStr.split('-');
  return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
}

// 定期タスクが指定日に該当するか判定
// monthly で「31日」など存在しない日付の場合はその月の月末に該当させる
export function recurringMatchesDate(rec, dateStr) {
  if (!rec || !dateStr) return false;
  const d = parseDateStr(dateStr);
  if (rec.type === 'daily') return true;
  if (rec.type === 'weekly') {
    return Array.isArray(rec.weekdays) && rec.weekdays.includes(d.getDay());
  }
  if (rec.type === 'monthly') {
    if (!rec.dayOfMonth) return false;
    const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    const effectiveDay = Math.min(rec.dayOfMonth, daysInMonth);
    return d.getDate() === effectiveDay;
  }
  return false;
}

// 定期タスクの繰り返し内容を短いラベルにする（バッジ表示用）
export function formatRecurLabel(rec) {
  if (!rec) return '';
  if (rec.type === 'daily') return '毎日';
  if (rec.type === 'weekly') {
    const days = (rec.weekdays || []).slice().sort((a, b) => a - b).map((w) => WEEKDAY_LABELS[w]);
    return days.length > 0 ? `毎週 ${days.join('・')}` : '毎週';
  }
  if (rec.type === 'monthly') {
    return rec.dayOfMonth ? `毎月${rec.dayOfMonth}日` : '毎月';
  }
  return '';
}

export const subscribeToRecurringTasks = (userId, callback) => {
  const q = query(getUserRecurringRef(userId), orderBy('order', 'asc'));
  return onSnapshot(
    q,
    (snapshot) => {
      const items = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(items);
    },
    (err) => {
      console.error('Recurring snapshot listener error:', err);
    }
  );
};

export const addRecurringTask = async (userId, rec) => {
  const ref = await addDoc(getUserRecurringRef(userId), {
    ...rec,
    lastGeneratedDate: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
};

export const updateRecurringTask = async (userId, recId, updates) => {
  const ref = doc(db, 'users', userId, 'recurringTasks', recId);
  await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });
};

export const deleteRecurringTask = async (userId, recId) => {
  const ref = doc(db, 'users', userId, 'recurringTasks', recId);
  await deleteDoc(ref);
};

// 該当日の定期タスクを「今日やること(today)」に生成する
// - lastGeneratedDate !== targetDate のものだけ生成（同日二重生成を防止）
// - 生成したtodayタスクには recurringId と recurLabel を付与
export const generateDueRecurringTasks = async (userId, appDate) => {
  const targetDate = appDate || localDateStr();

  const recSnap = await getDocs(getUserRecurringRef(userId));
  if (recSnap.empty) return [];

  // 既存todayタスクの最大orderを取得
  const todaySnap = await getDocs(
    query(getUserTasksRef(userId), where('category', '==', 'today'))
  );
  let maxOrder = 0;
  todaySnap.docs.forEach((d) => {
    const o = d.data().order || 0;
    if (o > maxOrder) maxOrder = o;
  });

  const batch = writeBatch(db);
  const created = [];
  let didWrite = false;

  recSnap.docs.forEach((d) => {
    const rec = { id: d.id, ...d.data() };
    if (rec.lastGeneratedDate === targetDate) return;
    if (!recurringMatchesDate(rec, targetDate)) return;

    maxOrder += 1;
    const newTaskRef = doc(getUserTasksRef(userId));
    batch.set(newTaskRef, {
      title: rec.title,
      category: 'today',
      completed: false,
      date: null,
      order: maxOrder,
      recurringId: rec.id,
      recurLabel: formatRecurLabel(rec),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    batch.update(d.ref, { lastGeneratedDate: targetDate, updatedAt: serverTimestamp() });
    created.push({ id: newTaskRef.id, title: rec.title });
    didWrite = true;
  });

  if (didWrite) await batch.commit();
  return created;
};

// ============================================================
// バケットリスト（bucketList）— 人生で達成したいことリスト
// 構造: users/{uid}/bucketList/{itemId}
// フィールド:
//   name(string)
//   targetYears(number 0-100)   目標年齢（年）
//   targetMonths(number 0-11)   目標年齢（月）
//   completed(boolean)
//   completedAt(timestamp|null)
//   createdAt(timestamp)
//   order(number)
// 生年月日は users/{uid}/settings/appState.birthdate に文字列 "YYYY-MM-DD" で保存
// ============================================================
const getUserBucketRef = (userId) => collection(db, 'users', userId, 'bucketList');

export const subscribeToBucketItems = (userId, callback) => {
  const q = query(getUserBucketRef(userId), orderBy('order', 'asc'));
  return onSnapshot(
    q,
    (snapshot) => {
      const items = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(items);
    },
    (err) => {
      console.error('BucketList snapshot listener error:', err);
    }
  );
};

export const addBucketItem = async (userId, item) => {
  const ref = await addDoc(getUserBucketRef(userId), {
    name: item.name,
    targetYears: item.targetYears || 0,
    targetMonths: item.targetMonths || 0,
    completed: false,
    completedAt: null,
    order: item.order || 0,
    createdAt: serverTimestamp(),
  });
  return ref.id;
};

export const updateBucketItem = async (userId, itemId, updates) => {
  const ref = doc(db, 'users', userId, 'bucketList', itemId);
  await updateDoc(ref, updates);
};

export const deleteBucketItem = async (userId, itemId) => {
  const ref = doc(db, 'users', userId, 'bucketList', itemId);
  await deleteDoc(ref);
};

export const completeBucketItem = async (userId, itemId) => {
  const ref = doc(db, 'users', userId, 'bucketList', itemId);
  await updateDoc(ref, {
    completed: true,
    completedAt: serverTimestamp(),
  });
};

export const restoreBucketItem = async (userId, itemId) => {
  const ref = doc(db, 'users', userId, 'bucketList', itemId);
  await updateDoc(ref, {
    completed: false,
    completedAt: null,
  });
};

// 生年月日（settings/appState.birthdate に格納）
export const getBirthdate = async (userId) => {
  const ref = getUserSettingsRef(userId);
  const snap = await getDoc(ref);
  if (snap.exists() && snap.data().birthdate) {
    return snap.data().birthdate;
  }
  return null;
};

export const setBirthdate = async (userId, birthdate) => {
  const ref = getUserSettingsRef(userId);
  await setDoc(ref, { birthdate, updatedAt: serverTimestamp() }, { merge: true });
};

export const subscribeToBirthdate = (userId, callback) => {
  const ref = getUserSettingsRef(userId);
  return onSnapshot(ref, (snap) => {
    if (snap.exists()) {
      callback(snap.data().birthdate || null);
    } else {
      callback(null);
    }
  });
};

// ============================================================
// チェックリスト（checklists）— 持ち物チェックなどのセット管理
// 構造: users/{uid}/checklists/{setId}（1セット＝1ドキュメント）
// フィールド:
//   name(string)          セット名（例：宿泊・日帰り）
//   order(number)         セットの並び順
//   categories(array)     [{ id, name, icon, items: [{ id, name, checked }] }]
//   createdAt, updatedAt(serverTimestamp)
// オフラインは enableIndexedDbPersistence（Firestore の IndexedDB キャッシュ）に乗る
// ============================================================
const getUserChecklistsRef = (userId) => collection(db, 'users', userId, 'checklists');

export const subscribeToChecklists = (userId, callback, onError) => {
  const q = query(getUserChecklistsRef(userId), orderBy('order', 'asc'));
  return onSnapshot(
    q,
    (snapshot) => {
      const sets = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(sets);
    },
    (err) => {
      console.error('Checklist snapshot listener error:', err);
      if (onError) onError(err);
    }
  );
};

export const addChecklistSet = async (userId, set) => {
  const ref = await addDoc(getUserChecklistsRef(userId), {
    name: set.name,
    order: set.order || 0,
    categories: set.categories || [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
};

export const updateChecklistSet = async (userId, setId, updates) => {
  const ref = doc(db, 'users', userId, 'checklists', setId);
  await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });
};

export const deleteChecklistSet = async (userId, setId) => {
  const ref = doc(db, 'users', userId, 'checklists', setId);
  await deleteDoc(ref);
};

// ============================================================
// 旅行費用（trip / budgets / expenses）— checklists ドキュメントへの任意フィールド追加
// 後方互換：これらのフィールドが無い旧ドキュメントは「費用未設計の普通のリスト」として扱う。
// 既存の name / order / categories には一切触らない（updateDoc で指定フィールドのみ更新）。
//
// フィールド構造（すべて任意）:
//   trip: {
//     tripName: string | null           旅行固有の名前（例：北海道オフ会。未設定＝セット名で記録・v1.9.3）
//     kind: 'private' | 'business'      事業/privateの区分
//     startDate: 'YYYY-MM-DD' | null    旅行の開始日
//     endDate:   'YYYY-MM-DD' | null
//     status: 'active' | 'done'         done＝完成（エクスポート済み）
//     exportedAt: 'YYYY-MM-DD HH:mm' | null
//   }
//   budgets:  [{ catId, name, icon, amount }]          費目別予算（マスタのスナップショット）
//   expenses: [{ id, catId, amount, memo, at }]        実費レコード（at はローカル日時文字列。
//                                                       配列内に serverTimestamp は置けない）
//
// プラン枠（v1.10.0）：各セットに最大3プランを並行保存。
//   枠1（slot1）＝上記のルートフィールドそのまま（既存データ＝枠1。移行処理なしで無損失）
//   枠2・枠3    ＝ tripSlots.slot2 / tripSlots.slot3 マップ（中身は同じ trip/budgets/expenses）
//   各ヘルパは slotKey 引数（既定 'slot1'）で書き込み先を切り替える。
//   パス生成は tripLogic.slotFieldPath に集約（UI 側の読み取りビューと必ず一致させる）
// ============================================================

// trip メタ情報（旅行名・区分・日付・状態）の部分更新（v1.15.1）。
// patch に含まれるフィールドだけをドット記法で書く（trip 全体は上書きしない）。
// 旧実装は trip 全体を丸ごと書いていたため、旅行名の onBlur 保存の直後に
// 区分ボタン等が古い画面状態で trip を書き直し、旅行名だけ消える不具合があった。
export const updateTripMeta = async (userId, setId, patch, slotKey = 'slot1') => {
  const fields = tripPatchFields(slotKey, patch);
  if (Object.keys(fields).length === 0) return;
  const ref = doc(db, 'users', userId, 'checklists', setId);
  await updateDoc(ref, { ...fields, updatedAt: serverTimestamp() });
};

// 費目別予算の保存（費目マスタのスナップショット。過去旅行はマスタ変更の影響を受けない）
export const saveTripBudgets = async (userId, setId, budgets, slotKey = 'slot1') => {
  const ref = doc(db, 'users', userId, 'checklists', setId);
  await updateDoc(ref, { [slotFieldPath(slotKey, 'budgets')]: budgets, updatedAt: serverTimestamp() });
};

// 実費の追加は arrayUnion（配列全書き換えをしない）
// → スマホ2台同時入力・オフライン同時編集でも上書き消失しない
// （枠2・3 のネストしたパスでも arrayUnion は同様に効く）
export const addTripExpense = async (userId, setId, expense, slotKey = 'slot1') => {
  const ref = doc(db, 'users', userId, 'checklists', setId);
  await updateDoc(ref, { [slotFieldPath(slotKey, 'expenses')]: arrayUnion(expense), updatedAt: serverTimestamp() });
};

// 実費の取り消し（id が一意なので完全一致の arrayRemove で安全に1件だけ消える）
export const removeTripExpense = async (userId, setId, expense, slotKey = 'slot1') => {
  const ref = doc(db, 'users', userId, 'checklists', setId);
  await updateDoc(ref, { [slotFieldPath(slotKey, 'expenses')]: arrayRemove(expense), updatedAt: serverTimestamp() });
};

// 枠のIDではなく、リセットまで維持される旅行固有IDを初回転記前に確定する。
// transactionで2端末の同時操作でも同じIDを返す。最新の保存内容で転記する。
export const prepareTripExport = async (userId, setId, slotKey = 'slot1') => {
  const ref = doc(db, 'users', userId, 'checklists', setId);
  const candidateId = crypto.randomUUID();
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('旅行が見つかりません。再読み込みしてください。');
    const view = getSlotView({ ...snap.data(), id: setId }, slotKey);
    if (!view.trip || (!view.budgets?.length && !view.expenses?.length)) {
      throw new Error('旅行の費用データがありません。再読み込みしてください。');
    }
    const recordId = view.trip.recordId || candidateId;
    if (!view.trip.recordId) {
      tx.update(ref, { [`${slotFieldPath(slotKey, 'trip')}.recordId`]: recordId });
    }
    return { ...view, trip: { ...view.trip, recordId } };
  });
};

// エクスポート完了の記録（ドット記法で trip の該当フィールドのみ更新）
export const markTripExported = async (userId, setId, exportedAt, slotKey = 'slot1') => {
  const ref = doc(db, 'users', userId, 'checklists', setId);
  const tripPath = slotFieldPath(slotKey, 'trip');
  await updateDoc(ref, {
    [`${tripPath}.status`]: 'done',
    [`${tripPath}.exportedAt`]: exportedAt,
    updatedAt: serverTimestamp(),
  });
};

// 費用データのリセット（該当枠の trip / budgets / expenses をフィールドごと削除）
// 持ち物リスト（categories）には一切触らない。エクスポート後の「次の旅行に備える」用
// 枠2・3 は tripSlots.slotN マップごと削除（空マップの残骸を残さない）
export const resetTripMoney = async (userId, setId, slotKey = 'slot1') => {
  const ref = doc(db, 'users', userId, 'checklists', setId);
  const k = normalizeSlotKey(slotKey);
  if (k === 'slot1') {
    await updateDoc(ref, {
      trip: deleteField(),
      budgets: deleteField(),
      expenses: deleteField(),
      updatedAt: serverTimestamp(),
    });
  } else {
    await updateDoc(ref, {
      [`tripSlots.${k}`]: deleteField(),
      updatedAt: serverTimestamp(),
    });
  }
};

// ============================================================
// 費目マスタ（settings/expenseCategories）— アプリ内で追加・編集・削除可能
// 初期値は社長確定の正式6費目（2026-07-08 確定。「その他」は作らない指示）
//   交通費＝新幹線・飛行機・電車・タクシー・ガソリン・駐車場
//   飲食費＝1人での食事・コンビニ・カフェ
//   会議費＝打合せのカフェ代・オフ会会費など
//   交際費＝飲み会・手土産など
//   観光費＝入場料・体験・買い物・お土産
// firestore.rules は既存の settings ルールで許可済み＝ルール変更不要
//
// color（色分け・2026-07-08 社長指示）：赤＝事業寄り／青＝private寄り／白＝両方（中立）
// 「白」はダークモードで見えないため「無色・ニュートラル（枠線のみ）」として扱う
// ============================================================
export const DEFAULT_EXPENSE_CATEGORIES = [
  { id: 'transport', name: '交通費', icon: '🚃', color: 'white' },
  { id: 'lodging', name: '宿泊費', icon: '🏨', color: 'white' },
  { id: 'dining', name: '飲食費', icon: '🍜', color: 'blue' },
  { id: 'meeting', name: '会議費', icon: '☕', color: 'red' },
  { id: 'social', name: '交際費', icon: '🍺', color: 'red' },
  { id: 'sightseeing', name: '観光費', icon: '🎡', color: 'blue' },
];

const getExpenseCategoriesRef = (userId) => doc(db, 'users', userId, 'settings', 'expenseCategories');

// 費目マスタのリアルタイム購読（未作成なら初期6費目を返す。書き込みはしない）
export const subscribeToExpenseCategories = (userId, callback) => {
  const ref = getExpenseCategoriesRef(userId);
  return onSnapshot(
    ref,
    (snap) => {
      if (snap.exists() && Array.isArray(snap.data().categories) && snap.data().categories.length > 0) {
        callback(snap.data().categories);
      } else {
        callback(DEFAULT_EXPENSE_CATEGORIES);
      }
    },
    (err) => {
      console.error('ExpenseCategories snapshot listener error:', err);
      callback(DEFAULT_EXPENSE_CATEGORIES);
    }
  );
};

export const saveExpenseCategories = async (userId, categories) => {
  const ref = getExpenseCategoriesRef(userId);
  await setDoc(ref, { categories, updatedAt: serverTimestamp() }, { merge: true });
};

// 既存の「毎日やるタスク(category==='daily')」を定期タスク(毎日)へ移行する
// 旧仕様の毎日タスクを失わないための一度きりの引っ越し（毎日タスクが無ければ何もしない）
export const migrateDailyTasksToRecurring = async (userId) => {
  const dailySnap = await getDocs(
    query(getUserTasksRef(userId), where('category', '==', 'daily'))
  );
  if (dailySnap.empty) return 0;

  const batch = writeBatch(db);
  let order = 0;
  dailySnap.docs.forEach((d) => {
    const data = d.data();
    order += 1;
    const recRef = doc(getUserRecurringRef(userId));
    batch.set(recRef, {
      title: data.title || '',
      type: 'daily',
      weekdays: [],
      dayOfMonth: null,
      order: data.order || order,
      lastGeneratedDate: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    batch.delete(d.ref);
  });
  await batch.commit();
  return dailySnap.size;
};
